import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiBarChart3, FiTarget, FiShield, FiUsers, FiBookOpen, FiDollarSign, FiGlobe, FiClock, FiCheck } = FiIcons;

function Services() {
  const services = [
    {
      icon: FiTrendingUp,
      title: 'Stock Trading',
      description: 'Professional stock market analysis and trading strategies for maximum returns.',
      image: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=500&h=300&fit=crop',
      features: ['Real-time market analysis', 'Portfolio optimization', 'Risk management', 'Performance tracking'],
      price: 'Starting at $99/month'
    },
    {
      icon: FiBarChart3,
      title: 'Forex Trading',
      description: 'Expert forex trading solutions with real-time market insights and analysis.',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=500&h=300&fit=crop',
      features: ['24/5 market access', 'Currency pair analysis', 'Leverage strategies', 'Economic calendar'],
      price: 'Starting at $149/month'
    },
    {
      icon: FiTarget,
      title: 'Crypto Trading',
      description: 'Navigate the cryptocurrency market with our advanced trading algorithms.',
      image: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=500&h=300&fit=crop',
      features: ['24/7 trading support', 'DeFi strategies', 'Altcoin analysis', 'Security protocols'],
      price: 'Starting at $199/month'
    },
    {
      icon: FiShield,
      title: 'Risk Management',
      description: 'Comprehensive risk assessment and management strategies to protect your investments.',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=500&h=300&fit=crop',
      features: ['Portfolio diversification', 'Stop-loss strategies', 'Risk assessment tools', 'Insurance options'],
      price: 'Starting at $79/month'
    },
    {
      icon: FiUsers,
      title: 'Portfolio Management',
      description: 'Professional portfolio management services tailored to your investment goals.',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=500&h=300&fit=crop',
      features: ['Asset allocation', 'Rebalancing services', 'Performance reporting', 'Tax optimization'],
      price: 'Starting at $299/month'
    },
    {
      icon: FiBookOpen,
      title: 'Trading Education',
      description: 'Comprehensive trading education programs for beginners to advanced traders.',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=500&h=300&fit=crop',
      features: ['Video tutorials', 'Live webinars', 'Trading simulators', 'Certification programs'],
      price: 'Starting at $49/month'
    },
  ];

  const additionalServices = [
    {
      icon: FiClock,
      title: '24/7 Support',
      description: 'Round-the-clock support from our expert trading team.'
    },
    {
      icon: FiGlobe,
      title: 'Global Markets',
      description: 'Access to international markets and trading opportunities.'
    },
    {
      icon: FiDollarSign,
      title: 'Cost Effective',
      description: 'Competitive pricing with transparent fee structures.'
    },
  ];

  return (
    <>
      <SEO 
        title="Trading Services - Stock, Forex, Crypto & Portfolio Management | TradePro"
        description="Professional trading services including stock trading, forex, cryptocurrency, risk management, and portfolio management. Expert guidance for all trading levels."
        keywords="trading services, stock trading, forex trading, crypto trading, portfolio management, risk management, trading education"
      />
      <div className="pt-16 lg:pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary-900 to-primary-700 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Trading Services</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Comprehensive trading solutions designed to help you succeed in today's dynamic financial markets. From beginner education to advanced portfolio management.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <SafeIcon icon={service.icon} className="h-12 w-12 text-primary-600 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Features:</h4>
                      <ul className="space-y-1">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="border-t pt-4">
                      <div className="text-lg font-bold text-primary-600">{service.price}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Choose Our Services?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Additional benefits that set our trading services apart from the competition.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {additionalServices.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="text-center p-6 bg-gray-50 rounded-xl"
                >
                  <SafeIcon icon={service.icon} className="h-16 w-16 text-primary-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Process */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Simple steps to get started with our professional trading services.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Consultation', description: 'Free initial consultation to understand your trading goals and experience level.' },
                { step: '02', title: 'Strategy Selection', description: 'Choose the trading services and strategies that best fit your objectives.' },
                { step: '03', title: 'Account Setup', description: 'Quick and secure account setup with full onboarding support.' },
                { step: '04', title: 'Start Trading', description: 'Begin trading with expert guidance and ongoing support from our team.' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary-900 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your Trading?
              </h2>
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                Join thousands of successful traders who have elevated their performance with our professional services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#contact"
                  className="inline-flex items-center px-8 py-4 bg-secondary-500 text-white font-semibold rounded-lg hover:bg-secondary-600 transition-colors"
                >
                  Get Free Consultation
                </a>
                <a
                  href="#services"
                  className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-900 transition-colors"
                >
                  Compare Services
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Services;